import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Calendar, ChevronDown, ChevronUp, LucideAngularModule, MapPin } from 'lucide-angular';
import type { ChristmasPartyItemModel } from '../../core/models/christmas-party-item.model';
import {
  type EventCategory,
  type EventItem,
  type EventType,
  type EventWithAgendaItems,
  type EventWithRegistrationDeadlineItem,
  isEventEventWithDescriptionMarkDownField,
  isEventWithAgendaField,
  isEventWithRegistrationDeadlineField,
} from '../../core/models/middleware/event-item.model';
import { EventService } from '../../core/services/event.service';
import { getEventCategoryLabel, mapEventTypeToCategory } from '../../core/utils/event-category.util';
import { LinkBoxComponent } from '../../shared/components/link-box/link-box.component';
import { MarkdownCardComponent } from '../../shared/components/markdown-card/markdown-card.component';
import { ProgressiveImageComponent } from '../../shared/components/progressive-image/progressive-image.component';
import { SharedCardComponent } from '../../shared/components/shared-card/shared-card.component';
import { UpdatesComponent } from '../updates/updates.component';

@Component({
  selector: 'app-christmas-party-overview',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    SharedCardComponent,
    MarkdownCardComponent,
    ProgressiveImageComponent,
    UpdatesComponent,
    LinkBoxComponent,
  ],
  templateUrl: './christmas-party-overview.component.html',
})
export class ChristmasPartyOverviewComponent {
  // Icons
  readonly events = Calendar;
  readonly mapPin = MapPin;
  readonly chevronDown = ChevronDown;
  readonly chevronUp = ChevronUp;

  readonly eventType: EventType = 'Weihnachtsfeier';
  readonly eventCategory: EventCategory = mapEventTypeToCategory(this.eventType);

  private route = inject(ActivatedRoute);
  private eventService = inject(EventService);

  // Event data
  eventDocumentId = this.route.snapshot.params.eventDocumentId as string;

  hasUpdates = false;

  eventLocation = computed(() => {
    const currentEvent = this.event();
    if (currentEvent && 'city' in currentEvent && 'country' in currentEvent) {
      return `${currentEvent.city}, ${currentEvent.country}`;
    }
    return '';
  });
  eventStartDate = computed(() => {
    const currentEvent = this.event();
    return currentEvent?.startDate;
  });
  eventEndDate = computed(() => {
    const currentEvent = this.event();
    return currentEvent?.endDate;
  });

  eventDateCompact = computed(() => {
    const startDate = this.eventStartDate();
    const endDate = this.eventEndDate();
    if (!startDate) return '';

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    // Format start date compactly with weekday
    const startWeekday = start.toLocaleDateString('de-DE', { weekday: 'short' });
    const startDay = start.getDate().toString().padStart(2, '0');
    const startMonth = (start.getMonth() + 1).toString().padStart(2, '0');
    let result = `${startWeekday}, ${startDay}.${startMonth}`;

    // If multi-day, add end date with year and weekday
    if (end && start.toDateString() !== end.toDateString()) {
      const endWeekday = end.toLocaleDateString('de-DE', { weekday: 'short' });
      const endDay = end.getDate().toString().padStart(2, '0');
      const endMonth = (end.getMonth() + 1).toString().padStart(2, '0');
      const endYear = end.getFullYear().toString().slice(-2);
      result += ` - ${endWeekday}, ${endDay}.${endMonth}.${endYear}`;
    }

    return result;
  });

  description = computed(() => {
    const currentEvent = this.event();
    if (currentEvent && isEventEventWithDescriptionMarkDownField(currentEvent)) {
      return currentEvent.description;
    }
    return undefined;
  });

  // Description expansion state
  isDescriptionExpanded = signal(false);

  // Toggle description expansion
  toggleDescription() {
    this.isDescriptionExpanded.update((current) => !current);
  }

  // Computed property for description display
  displayDescription = computed(() => {
    const description = this.description() || '';
    const maxLength = 150;

    if (this.isDescriptionExpanded() || description.length <= maxLength) {
      return description;
    }

    return `${description.substring(0, maxLength)}...`;
  });

  // Check if description needs expansion
  needsExpansion = computed(() => {
    const description = this.event()?.description || '';
    return description.length > 150;
  });

  moodImageData = computed(() => {
    const currentEvent = this.event();
    if (currentEvent?.moodImage) {
      return {
        url: currentEvent.moodImage.url,
        thumbnailUrl: currentEvent.moodImage.thumbnailUrl,
        width: currentEvent.moodImage.width,
        height: currentEvent.moodImage.height,
        alt: `${currentEvent.title} mood image`,
      };
    }
    return null;
  });

  event = computed(() => this.eventService.getCurrentEvent() as ChristmasPartyItemModel);

  agenda = computed(() => {
    const currentEvent = this.eventService.getCurrentEvent();
    if (currentEvent && isEventWithAgendaField(currentEvent)) {
      return (currentEvent as EventWithAgendaItems<EventType>).agenda;
    } else {
      return [];
    }
  });

  constructor() {
    if (this.eventService.getCurrentEvent()?.documentId === this.eventDocumentId) {
      // Display stored event until the refresh is doneEventWithAgendaItems
      this.eventService.setCurrentEvent(this.eventService.getCurrentEvent() as ChristmasPartyItemModel);
    } else {
      this.eventService.clearCurrentEvent();
    }

    this.eventService.getEvent(this.eventDocumentId, this.eventType).subscribe({
      next: (data) => {
        if (data) {
          this.eventService.setCurrentEvent(data as ChristmasPartyItemModel);
        } else {
          // If we don't receive event data, we need to clear old data, to prevent us from displaying the wrong data
          this.eventService.clearCurrentEvent();
        }
      },
      error: (error) => {
        this.eventService.clearCurrentEvent();
        // TODO [#215]: use winston for logging
        console.error(`Error loading ${this.eventType} event:`, error);
        throw error;
      },
      complete: () => {
        if (
          !this.eventService.getCurrentEvent() ||
          this.eventDocumentId !== this.eventService.getCurrentEvent()?.documentId
        ) {
          this.eventService.clearCurrentEvent();
          throw Error(`Event with id ${this.eventDocumentId} not found`);
        }
      },
    });

    // Refresh current time for signals every 5 seconds
    effect(() => {
      const intervalId = setInterval(() => {
        this.now.set(new Date());
      }, 5000);
      return () => clearInterval(intervalId);
    });
  }

  public eventHasRegistrationDeadline(
    event: EventItem<EventType>
  ): event is EventWithRegistrationDeadlineItem<EventType> {
    return !!event && isEventWithRegistrationDeadlineField(event);
  }

  private now = signal(new Date());
  registerButtonActivated = computed(() => {
    if (!this.event() || !isEventWithRegistrationDeadlineField(this.event()!)) {
      return false;
    }

    const deadline =
      (this.event() as EventWithRegistrationDeadlineItem<EventType>)!.registrationDeadline.registrationDeadline.getTime();

    return this.now().getTime() <= deadline;
  });
  protected readonly getEventCategoryLabel = getEventCategoryLabel;
}
